// src/lib/auth/ProtectedRoute.jsx
import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import useRole from './useRole';
import { accessIsAllowed } from './roles';
import { useAuth } from '../auth';

export default function ProtectedRoute({ children, allowedRoles = [], allowedProfileFlags = [] }) {
  const location = useLocation();
  const userRole = useRole();
  const { user, loading, profile } = useAuth();

  // A tiny state bump to re-render when the grace window ends
  const [, forceRerender] = useState(0);

  // Store a "wait until" timestamp, so the decision is available immediately during render
  const waitUntilRef = useRef(null);

  const shouldWaitForProfile = !loading && !!user && !profile; // profile not hydrated yet

  // Start the grace window immediately (synchronously) the first time we detect profile missing
  if (shouldWaitForProfile && !waitUntilRef.current) {
    waitUntilRef.current = Date.now() + 10000; // 10s grace (your preference)
  }

  // If profile arrives, clear the grace window
  if (profile && waitUntilRef.current) {
    waitUntilRef.current = null;
  }

  // Set a timer to re-render when grace expires (so we can fall through to not-authorized if needed)
  useEffect(() => {
    if (!waitUntilRef.current) return;

    const ms = Math.max(0, waitUntilRef.current - Date.now());
    const t = setTimeout(() => forceRerender((x) => x + 1), ms);

    return () => clearTimeout(t);
  }, [profile, user, loading]);

  // 1) Wait for Firebase auth
  if (loading) {
    return (
      <Box sx={{ minHeight: '50vh', display: 'grid', placeItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  // 2) Not logged in
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // 3) Logged in, but profile not ready yet -> wait (immediately, on first render)
  if (shouldWaitForProfile) {
    // If grace expired and profile still missing, treat as not authorized (or you could route to an error page)
    if (waitUntilRef.current && Date.now() > waitUntilRef.current) {
      return <Navigate to="/not-authorized" replace />;
    }

    return (
      <Box sx={{ minHeight: '50vh', display: 'grid', placeItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  // 4) Access gate (profile is present at this point): role OR a per-user access flag on the profile
  if (!accessIsAllowed(userRole, allowedRoles, profile, allowedProfileFlags)) {
    return <Navigate to="/not-authorized" replace />;
  }

  return children;
}
